import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Lock, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface DemoRewardMilestoneProps {
  days: number;
  translationKey: string;
  longestStreak: number;
  isNextMilestone: boolean;
  onModalOpen?: () => void;
  onModalClose?: () => void;
}

export function DemoRewardMilestone({
  days,
  translationKey,
  longestStreak,
  isNextMilestone,
  onModalOpen,
  onModalClose,
}: DemoRewardMilestoneProps) {
  const { t } = useLanguage();
  const isUnlocked = longestStreak >= days;
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (showModal) {
      onModalOpen?.();
    } else {
      onModalClose?.();
    }
  }, [showModal, onModalOpen, onModalClose]);

  const progressPercent = isUnlocked ? 100 : Math.min((longestStreak / days) * 100, 100);

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className={`
          w-full p-4 rounded-lg border text-left transition-all
          ${isUnlocked 
            ? 'bg-primary/10 border-primary/30' 
            : isNextMilestone 
              ? 'bg-card border-border' 
              : 'bg-muted/30 border-border/50 opacity-60'
          }
        `}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`
              w-10 h-10 rounded-full flex items-center justify-center shrink-0
              ${isUnlocked ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}
            `}>
              {isUnlocked ? <Check className="h-5 w-5" /> : <Lock className="h-4 w-4" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className={`font-medium truncate ${isUnlocked ? 'text-foreground' : 'text-muted-foreground'}`}>
                {t(translationKey)}
              </p>
            </div>
          </div>

          {/* Right side: Unlocked badge OR progress bar for next milestone */}
          {isUnlocked ? (
            <div className="w-20 shrink-0 text-center py-2 px-2 rounded bg-primary text-primary-foreground">
              <p className="text-xs font-medium">✔️ {t('rewardUnlocked')}</p>
            </div>
          ) : isNextMilestone ? (
            <div className="w-20 shrink-0">
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground text-center mt-1">
                {longestStreak}/{days}
              </p>
            </div>
          ) : null}
        </div>
      </button>

      {/* Demo Reward Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center">
              {isUnlocked ? t('demoRewardTitle') : t(translationKey)}
            </DialogTitle>
          </DialogHeader>
          
          <div className="text-center py-4">
            {isUnlocked ? (
              <>
                <div className="text-4xl mb-4">🎁</div>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('demoRewardDesc')}
                </p>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm font-medium">{t('demoRewardExample')}</p>
                </div>
              </>
            ) : (
              <>
                <div className="text-4xl mb-4">🔒</div>
                <p className="text-sm text-muted-foreground">
                  {t('rewardLocked')}
                </p>
                <p className="text-lg font-bold text-primary mt-2">
                  {days - longestStreak} {t('dashboardDays')}
                </p>
              </>
            )}
          </div>

          <Button onClick={() => setShowModal(false)} className="w-full">
            {t('healthBenefitClose')}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
